// src/shared/components/ServiceSidebar.tsx
// Shell premium do Projectlys (Tailwind + tokens + dark mode)

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { BRAND } from '../config/brand';
import { Avatar } from './ui/Avatar';
import { ThemeToggle } from './ui/ThemeToggle';
import { useAdminModal } from './AdminModal';
import { useGlobalAuth } from '../contexts/GlobalAuthContext';
import { cn } from '../lib/cn';

interface NavigationItem {
  path: string;
  label: string;
  icon: string;
  description?: string;
  external?: boolean;
}

export type { NavigationItem };

interface ServiceSidebarProps {
  serviceTitle: string;
  serviceIcon: string;
  userName?: string;
  userRole?: string;
  children?: React.ReactNode;
  navigationItems?: NavigationItem[];
}

const I = {
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  tasks: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="6" /><rect x="13" y="7" width="3" height="10" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  grid: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  timeline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
};

function iconForPath(path: string): React.ReactNode {
  if (path === ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR) return I.calendar;
  if (path === ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD) return I.tasks;
  if (path === ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS) return I.clock;
  if (path === ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN) return I.timeline;
  if (path === ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD) return I.chart;
  if (path === ROUTES.QUALITY_MANAGEMENT.ADMIN) return I.settings;
  return I.grid;
}

function testIdForPath(path: string): string {
  const seg = path.split('/').filter(Boolean).pop() || 'item';
  return `nav-${seg}`;
}

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

const ServiceSidebar: React.FC<ServiceSidebarProps> = ({ userName, userRole, children, navigationItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openAdmin } = useAdminModal();
  const { logout } = useGlobalAuth();

  const isActive = (item: NavigationItem) => !item.external && location.pathname === item.path;

  // Usa o logout central do contexto (encerra o sessionManager e limpa o
  // estado num só lugar) em vez de um fetch paralelo com limpeza manual.
  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      window.location.href = '/';
    }
  };

  return (
    <aside className="flex h-screen w-64 shrink-0 flex-col border-r border-border bg-surface-1">
      {/* Header / marca */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-brand-fg">
            <svg viewBox="0 0 40 40" fill="none" className="h-5 w-5">
              <rect x="4" y="16" width="6" height="20" rx="2" fill="currentColor" opacity="0.5" />
              <rect x="13" y="10" width="6" height="26" rx="2" fill="currentColor" opacity="0.7" />
              <rect x="22" y="6" width="6" height="30" rx="2" fill="currentColor" opacity="0.85" />
              <rect x="31" y="4" width="6" height="32" rx="2" fill="currentColor" />
            </svg>
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-foreground">{BRAND.name}</span>
        </div>
        <ThemeToggle />
      </div>

      {/* Usuário */}
      {userName && (
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Avatar name={userName} size="md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{userName}</p>
            {userRole && <p className="truncate text-xs capitalize text-foreground-muted">{userRole}</p>}
          </div>
        </div>
      )}

      {/* Conteúdo customizado + navegação */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {children}
        {navigationItems && navigationItems.length > 0 && (
          <nav className="flex flex-col gap-1">
            <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Navegação</p>
            {navigationItems.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.path}
                  data-testid={testIdForPath(item.path)}
                  onClick={() => {
                    if (item.path === ROUTES.QUALITY_MANAGEMENT.ADMIN) {
                      openAdmin();
                      return;
                    }
                    if (active) return;
                    if (item.external) window.location.href = item.path;
                    else navigate(item.path);
                  }}
                  className={cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                    active
                      ? 'bg-brand/10 text-brand'
                      : 'text-foreground-muted hover:bg-surface-3 hover:text-foreground'
                  )}
                >
                  <span className={cn('h-5 w-5 shrink-0', active ? 'text-brand' : 'text-foreground-subtle group-hover:text-foreground')}>
                    {iconForPath(item.path)}
                  </span>
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}
      </div>

      {/* Footer / logout */}
      <div className="border-t border-border px-3 py-3">
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-muted transition-colors hover:bg-danger/10 hover:text-danger"
        >
          <LogoutIcon />
          Sair
        </button>
      </div>
    </aside>
  );
};

export default ServiceSidebar;
